/**
 * A service for managing local media stream (webcam and screen sharing streams).
 * This class provides methods to set and retrieve local media streams.
 */
export default class MediaTrackService {
  /**
   * An object that stores references to local media streams.
   * Keys represent the type of media (e.g., "webcam", "screen").
   * @static
   * @type {Object<string, MediaStream>}
   */
  static localMediaStreams = {};

  /**
   * Sets the local webcam stream.
   *
   * @static
   * @function setLocalWebcamStream
   * @param {MediaStream} stream - The MediaStream object representing the local webcam stream.
   * @example
   * const webcamStream = await navigator.mediaDevices.getUserMedia({ video: true });
   * MediaTrackService.setLocalWebcamStream(webcamStream);
   */
  static setLocalWebcamStream = (stream) => {
    MediaTrackService.localMediaStreams["webcam"] = stream;
  };

  /**
   * Sets the local screen sharing stream.
   *
   * @static
   * @function setLocalScreenshareStream
   * @param {MediaStream} stream - The MediaStream object representing the local screen sharing stream.
   * @example
   * const screenshareStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
   * MediaTrackService.setLocalScreenshareStream(screenshareStream);
   */
  static setLocalScreenshareStream = (stream) => {
    MediaTrackService.localMediaStreams["screen"] = stream;
  };

  /**
   * Retrieves the local webcam stream.
   *
   * @static
   * @function getLocalWebcamStream
   * @returns {MediaStream|null} The MediaStream object for the local webcam stream, or `null` if not set.
   * @example
   * const webcamStream = MediaTrackService.getLocalWebcamStream();
   * if (webcamStream) {
   *   console.log('Webcam stream retrieved:', webcamStream);
   * } else {
   *   console.log('No webcam stream available.');
   * }
   */
  static getLocalWebcamStream = () => {
    return MediaTrackService.localMediaStreams["webcam"];
  };

  /**
   * Retrieves the local screen sharing stream.
   *
   * @static
   * @function getLocalScreenshareStream
   * @returns {MediaStream|null} The MediaStream object for the local screen sharing stream, or `null` if not set.
   * @example
   * const screenshareStream = MediaTrackService.getLocalScreenshareStream();
   * if (screenshareStream) {
   *   console.log('Screen sharing stream retrieved:', screenshareStream);
   * } else {
   *   console.log('No screen sharing stream available.');
   * }
   */
  static getLocalScreenshareStream = () => {
    return MediaTrackService.localMediaStreams["screen"];
  };
}
